import {DEEP_LINK} from '@assets/const/deepLink';
import {getCurrentTabInfo} from '@scripttag/helpers/actionWidgetHelpers';
import {
  getWidgetSettingsStorage,
  setWidgetSettingsStorage
} from '@scripttag/helpers/sessionHelpers';
import {onChatTool} from '@assets/helpers/eventBridge';
import extractGraphQlId from '@functions/helpers/utils/extractGraphQlId';

export default class ChattyJSService {
  constructor() {
    this.handlers = {};
    this.debug = false; // process.env.NODE_ENV === 'development';
    this.unsubscribeFromTool = null;
    this.messageQueue = []; // Queue for messages when chat is closed

    // Rate limiting configuration
    this.rateLimiting = {
      enabled: true,
      perBlockLimits: new Map(), // Track limits per blockId
      globalLastMessage: 0,
      GLOBAL_RATE_LIMIT_MS: 500, // Min 500ms between any messages
      PER_BLOCK_RATE_LIMIT_MS: 1000, // Min 1s between messages from same block
      MAX_MESSAGES_PER_MINUTE: 20, // Max 20 messages per minute globally
      messageTimestamps: [] // Track message timestamps for per-minute limit
    };

    this.initToolListener();
  }

  initToolListener() {
    // Listen for tool calls from the chat system
    this.unsubscribeFromTool = onChatTool(payload => {
      if (this.debug) console.log('[ChattyJS] Tool call received:', payload);

      if (payload.name === 'add_to_cart') {
        this.handleAddToCart(payload.data);
      }
    });
  }

  async handleAddToCart(lineItems) {
    if (this.debug) console.log('[ChattyJS] Handling add_to_cart:', lineItems);

    try {
      // Validate lineItems
      if (!Array.isArray(lineItems) || lineItems.length === 0) {
        throw new Error('Invalid lineItems: must be a non-empty array');
      }

      // Prepare the request data according to Shopify Cart API
      const formData = {
        items: lineItems.map(item => ({
          id: extractGraphQlId(item.variant_id, true),
          quantity: item.quantity
        }))
      };

      if (this.debug) console.log('[ChattyJS] Form data prepared:', formData);

      const cartAddUrl = `${window.location.origin}${window.Shopify.routes.root}cart/add.js`;

      // Make the AJAX request to Shopify Cart API
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: {'Content-Type': 'application/json', Accept: 'application/json'},
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        // Try to parse error details returned by Shopify (e.g., 422 with message/description)
        let errorPayload = null;
        try {
          errorPayload = await response.json();
        } catch {
          try {
            const text = await response.text();
            if (text) errorPayload = {message: text};
          } catch {
            // ignore
          }
        }

        const errorDetails = {
          status: response.status,
          message: errorPayload?.message || errorPayload?.description
        };
        const errorEvent = {success: false, details: errorDetails};
        if (this.hasHandler('onAddToCartResult')) this.call('onAddToCartResult', errorEvent);
        return errorEvent;
      }

      const result = await response.json();

      if (this.debug) console.log('[ChattyJS] Cart add successful:', result);

      // Emit success event or call success handler if available
      const successEvent = {success: true, details: result};
      if (this.hasHandler('onAddToCartResult')) this.call('onAddToCartResult', successEvent);
      return successEvent;
    } catch (error) {
      console.error('[ChattyJS] Error adding to cart:', error);

      // Emit error event or call the error handler if available
      const unknownErrorEvent = {success: false, details: {message: error.message}};
      if (this.hasHandler('onAddToCartResult')) this.call('onAddToCartResult', unknownErrorEvent);
      return unknownErrorEvent;
    }
  }

  registerHandler(name, handler) {
    if (this.debug) console.log(`[ChattyJS] Registering handler: ${name}`);
    this.handlers[name] = handler;
    return this;
  }

  unregisterHandler(name) {
    if (this.debug) console.log(`[ChattyJS] Unregistering handler: ${name}`);
    delete this.handlers[name];
    return this;
  }

  hasHandler(name) {
    return typeof this.handlers[name] === 'function';
  }

  call(handlerName, ...args) {
    if (!this.hasHandler(handlerName)) {
      console.warn(`[ChattyJS] ${handlerName} handler not registered`);
      return {success: false, error: 'Handler not available'};
    }

    try {
      const result = this.handlers[handlerName](...args);
      return result || {success: true};
    } catch (error) {
      console.error(`[ChattyJS] Error in ${handlerName}:`, error);
      return {success: false, error: error.message};
    }
  }

  #_checkIsOpen() {
    const tabInfo = getWidgetSettingsStorage();
    return tabInfo.open;
  }
  #_clickWidgetTrigger() {
    const widgetTrigger = document.getElementById('Avada-FAQ_WidgetTrigger');
    if (!widgetTrigger) return;
    widgetTrigger.click();
  }

  #_openPage(page) {
    if (!this.hasHandler('openPage')) {
      console.warn('[ChattyJS] openPage handler not registered');
      return {success: false, error: 'Handler not available'};
    }
    try {
      const currentPageInfo = getCurrentTabInfo(page);
      const result = this.handlers.openPage(currentPageInfo.page);
      return result || {success: true};
    } catch (error) {
      console.error('[ChattyJS] Error in openPage:', error);
      return {success: false, error: error.message};
    }
  }

  openWidget(page = DEEP_LINK[0]) {
    if (!DEEP_LINK.includes(page)) return;
    if (!this.#_checkIsOpen()) this.#_clickWidgetTrigger();
    if (this.hasHandler('openPage')) return this.#_openPage(page);
    const currentPageInfo = getCurrentTabInfo(page);
    const isHomePage = currentPageInfo.page === 'homePage';
    const isHelpPage = currentPageInfo.page === 'categoriesList';
    setWidgetSettingsStorage({
      open: true,
      currentTab: currentPageInfo.tab,
      currentPage: currentPageInfo.page,
      history: ['homePage', !isHomePage && currentPageInfo.page].filter(Boolean),
      ...(!isHelpPage && {isExpand: false})
    });
  }

  closeWidget() {
    if (this.#_checkIsOpen()) this.#_clickWidgetTrigger();
    setWidgetSettingsStorage({open: false});
  }

  /**
   * Check if rate limit is exceeded
   * @private
   * @param {string} blockId - Block ID to check rate limit for
   * @return {Object} Object with allowed flag and reason if not allowed
   */
  checkRateLimit(blockId = 'global') {
    if (!this.rateLimiting.enabled) {
      return {allowed: true};
    }

    const now = Date.now();

    // Check global rate limit
    if (now - this.rateLimiting.globalLastMessage < this.rateLimiting.GLOBAL_RATE_LIMIT_MS) {
      return {
        allowed: false,
        reason: `Global rate limit: Please wait ${
          this.rateLimiting.GLOBAL_RATE_LIMIT_MS - (now - this.rateLimiting.globalLastMessage)
        }ms`
      };
    }

    // Check per-block rate limit
    const blockLastMessage = this.rateLimiting.perBlockLimits.get(blockId) || 0;
    if (now - blockLastMessage < this.rateLimiting.PER_BLOCK_RATE_LIMIT_MS) {
      return {
        allowed: false,
        reason: `Block rate limit: Please wait ${
          this.rateLimiting.PER_BLOCK_RATE_LIMIT_MS - (now - blockLastMessage)
        }ms`
      };
    }

    // Check per-minute rate limit
    const oneMinuteAgo = now - 60000;
    this.rateLimiting.messageTimestamps = this.rateLimiting.messageTimestamps.filter(
      ts => ts > oneMinuteAgo
    );

    if (this.rateLimiting.messageTimestamps.length >= this.rateLimiting.MAX_MESSAGES_PER_MINUTE) {
      return {
        allowed: false,
        reason: `Per-minute rate limit: Maximum ${this.rateLimiting.MAX_MESSAGES_PER_MINUTE} messages per minute`
      };
    }

    return {allowed: true};
  }

  /**
   * Update rate limit trackers
   * @private
   * @param {string} blockId - Block ID to update
   */
  updateRateLimitTrackers(blockId = 'global') {
    const now = Date.now();
    this.rateLimiting.globalLastMessage = now;
    this.rateLimiting.perBlockLimits.set(blockId, now);
    this.rateLimiting.messageTimestamps.push(now);

    // Clean up old block limits (older than 5 minutes)
    const fiveMinutesAgo = now - 300000;
    for (const [id, timestamp] of this.rateLimiting.perBlockLimits.entries()) {
      if (timestamp < fiveMinutesAgo) {
        this.rateLimiting.perBlockLimits.delete(id);
      }
    }
  }

  /**
   * Open widget and send a message
   * @param {string} message - The message to send
   * @param {string} page - The page to open (default: '#chatty-chat')
   * @param {Object} additionalProperties - Additional properties for the message
   * @param {string} blockId - Optional block ID for targeting specific chat instances (TODO: implement targeting logic)
   * @return {Object} Result object with success status
   */
  openWidgetAndSendMessage({
    message,
    page = '#chatty-chat',
    additionalProperties = null,
    blockId = ''
  }) {
    if (this.debug)
      console.log('[ChattyJS] Opening widget and sending message:', {
        message,
        page,
        additionalProperties,
        blockId
      });

    // Check rate limiting
    const rateLimitCheck = this.checkRateLimit(blockId || 'global');
    if (!rateLimitCheck.allowed) {
      console.warn('[ChattyJS] Rate limit exceeded:', rateLimitCheck.reason);
      return {
        success: false,
        error: 'Rate limit exceeded',
        reason: rateLimitCheck.reason
      };
    }

    try {
      // Update rate limit trackers
      this.updateRateLimitTrackers(blockId || 'global');

      // First open the widget
      this.openWidget(page);

      // Wait for ChatPage to be ready and then send the message
      this.waitForChatPageAndSend({message, additionalProperties, blockId});

      return {success: true, message: 'Widget opened, message will be sent when ready'};
    } catch (error) {
      console.error('[ChattyJS] Error opening widget and sending message:', error);
      return {success: false, error: error.message};
    }
  }

  /**
   * Wait for ChatPage to be ready and then send message
   * @private
   */
  waitForChatPageAndSend({message, additionalProperties, blockId, maxAttempts = 50, attempt = 0}) {
    if (this.debug)
      console.log(`[ChattyJS] Waiting for ChatPage... (attempt ${attempt + 1}/${maxAttempts})`);

    // Check if ChatPage is ready
    if (this.isChatPageReady()) {
      if (this.debug) console.log('[ChattyJS] ChatPage is ready, sending message...');
      this.sendMessageAfterOpen({message, additionalProperties});
      return;
    }

    // If we've reached max attempts, queue the message
    if (attempt >= maxAttempts) {
      console.warn('[ChattyJS] ChatPage not ready after max attempts, queuing message');
      this.queueMessage({message, additionalProperties, blockId});
      return;
    }

    // Wait a bit and try again
    setTimeout(() => {
      this.waitForChatPageAndSend({
        message,
        additionalProperties,
        blockId,
        maxAttempts,
        attempt: attempt + 1
      });
    }, 200);
  }

  /**
   * Check if ChatPage is ready to receive messages
   * @private
   */
  isChatPageReady() {
    // Check if ChatPage handler is available
    if (
      window.ChatPageHandler &&
      typeof window.ChatPageHandler.handleSendDefaultMessage === 'function'
    ) {
      return true;
    }

    // Check if ChatPage ref is available
    if (window.chatPageRef && window.chatPageRef.current) {
      return true;
    }

    // Check if there's a ChatPage element in the DOM
    const chatPageElement = document.querySelector('[data-chat-page]');
    if (chatPageElement && chatPageElement.chatPageHandler) {
      return true;
    }

    return false;
  }

  /**
   * Send message after widget is opened
   * @private
   */
  sendMessageAfterOpen({message, additionalProperties}) {
    if (this.debug) console.log('[ChattyJS] Sending message after widget open...');

    // Use the immediate sending method since chat should be open now
    try {
      const result = this.sendMessageImmediate({
        message,
        additionalProperties
      });
      if (this.debug) console.log('[ChattyJS] Message sent after open:', result);
    } catch (error) {
      console.error('[ChattyJS] Error sending message after open:', error);
      // Fall back to direct method
      this.sendMessageDirect({
        message,
        additionalProperties
      });
    }
  }

  /**
   * Queue a message to be sent when the chat opens
   * @param {string} message - The message to queue
   * @param {Object} additionalProperties - Additional properties for the message
   * @param {string} blockId - Optional block ID for targeting specific chat instances (TODO: implement targeting logic)
   */
  queueMessage({message, additionalProperties = null, blockId = ''}) {
    if (this.debug)
      console.log('[ChattyJS] Queuing message:', {message, additionalProperties, blockId});

    this.messageQueue.push({
      message,
      additionalProperties: additionalProperties || {},
      blockId,
      timestamp: Date.now()
    });
  }

  /**
   * Used at ChatPage, don't remove this method
   * Process queued messages when service becomes ready
   * @private
   */
  processQueuedMessagesWhenReady() {
    if (this.messageQueue.length > 0) {
      if (this.debug) console.log('[ChattyJS] Service is ready, processing queued messages');
      this.processMessageQueue();
    }
  }

  /**
   * Check if the chat is currently open
   * @returns {boolean} True if chat is open
   */
  isChatOpen() {
    return this.#_checkIsOpen();
  }

  /**
   * Send a message to the chat
   * @param {string} message - The message to send
   * @param {Object} additionalProperties - Additional properties for the message
   * @param {string} blockId - Optional block ID for targeting specific chat instances (TODO: implement targeting logic)
   * @returns {Object} Result object with success status
   * @example
   * Send a simple message
   * ChattyJS.sendMessage('Hello, how can I help you?');
   *
   * Send a message with additional properties
   * ChattyJS.sendMessage('Check this product', {
   *   products: [{id: '123', title: 'Sample Product'}]
   * });
   */
  sendMessage({message, additionalProperties = null, blockId = ''}) {
    if (this.debug)
      console.log('[ChattyJS] Sending message:', {
        message,
        additionalProperties,
        blockId
      });

    try {
      if (!message || typeof message !== 'string') {
        throw new Error('Message must be a non-empty string');
      }

      // Ensure ChatPage is ready (has overridden handlers)
      if (!this.isChatPageReady()) {
        if (this.debug) console.log('[ChattyJS] ChatPage not ready, queuing message');
        this.queueMessage({message, additionalProperties, blockId});
        return {success: true, message: 'Message queued - ChatPage not ready'};
      }

      // Check if chat is open
      if (!this.isChatOpen()) {
        if (this.debug) console.log('[ChattyJS] Chat is closed, queuing message');
        this.queueMessage({message, additionalProperties, blockId});
        return {success: true, message: 'Message queued for when chat opens'};
      }

      // Chat is open and ChatPage is ready, try to send immediately
      return this.sendMessageImmediate({message, additionalProperties});
    } catch (error) {
      console.error('[ChattyJS] Error sending message:', error);
      const errorResult = {success: false, error: error.message};
      if (this.hasHandler('onMessageError')) this.call('onMessageError', errorResult);
      return errorResult;
    }
  }

  /**
   * Send message immediately (when chat is open)
   * @private
   */
  sendMessageImmediate({message, additionalProperties}) {
    if (this.debug) console.log('[ChattyJS] Sending message immediately...');

    // Try direct method first (most reliable)
    if (this.isChatPageReady()) {
      try {
        this.sendMessageDirect({message, additionalProperties});
        return {success: true, message: 'Message sent directly'};
      } catch {
        if (this.debug) console.log('[ChattyJS] Direct method failed, trying event system...');
      }
    }
  }

  enableDebug() {
    this.debug = true;
  }

  disableDebug() {
    this.debug = false;
  }

  /**
   * Direct message sending - bypasses event bridge entirely
   * This is a workaround for when the event system doesn't work
   * @param {string} message - The message to send
   * @param {Object} additionalProperties - Additional properties for the message
   * @returns {Object} Result object with success status
   */
  sendMessageDirect({message, additionalProperties = null}) {
    if (this.debug)
      console.log('[ChattyJS] Sending message directly:', {
        message,
        additionalProperties
      });

    try {
      if (!message || typeof message !== 'string') {
        throw new Error('Message must be a non-empty string');
      }

      // Try to find and call the ChatPage message handler directly
      if (typeof window !== 'undefined') {
        // Look for ChatPage in various places
        let chatPageHandler = null;

        // Check if there's a global ChatPage handler
        if (window.ChatPageHandler) {
          chatPageHandler = window.ChatPageHandler;
        }

        // Check if there's a ChatPage ref
        if (window.chatPageRef && window.chatPageRef.current) {
          chatPageHandler = window.chatPageRef.current;
        }

        // Check if there's a ChatPage instance in the DOM
        const chatPageElement = document.querySelector('[data-chat-page]');
        if (chatPageElement && chatPageElement.chatPageHandler) {
          chatPageHandler = chatPageElement.chatPageHandler;
        }

        if (chatPageHandler && typeof chatPageHandler.handleSendDefaultMessage === 'function') {
          console.log('[ChattyJS] Found ChatPage handler, calling directly');
          chatPageHandler.handleSendDefaultMessage({
            phrase: message,
            variables: {},
            additionalProperties: additionalProperties || {}
          });
          return {success: true, message: 'Message sent directly to ChatPage'};
        } else {
          console.warn('[ChattyJS] ChatPage handler not found, falling back to event system');
          return this.sendMessage({message, additionalProperties});
        }
      } else {
        throw new Error('Window not available');
      }
    } catch (error) {
      console.error('[ChattyJS] Error sending message directly:', error);
      return {success: false, error: error.message};
    }
  }

  destroy() {
    // Clean up event listeners
    if (this.unsubscribeFromTool) {
      this.unsubscribeFromTool();
      this.unsubscribeFromTool = null;
    }
  }
}
